import type { Quotation } from "@/mocks/quotationsData";

type Props = {
  quotations?: Quotation[];
  statsData?: {
    total: number;
    pending: number;
    accepted: number;
    totalValue: number;
  };
};

export function QuotationStatsBar({ quotations = [], statsData }: Props) {
  const total = statsData ? statsData.total : quotations.length;
  const pending = statsData
    ? statsData.pending
    : quotations.filter((q) => q.status === "Draft" || q.status === "Sent").length;
  const accepted = statsData
    ? statsData.accepted
    : quotations.filter((q) => q.status === "Accepted").length;

  const totalValue = statsData
    ? statsData.totalValue
    : quotations.reduce((sum, q) => {
        if (q.total_amount || (q as any).total) {
          return sum + Number(q.total_amount || (q as any).total || 0);
        }
        const items = q.items || [];
        const subtotal = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.rate) || 0), 0);
        const taxTotal = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.rate) || 0) * ((Number(i.taxPercent) || 0) / 100), 0);
        return sum + subtotal + taxTotal - (Number(q.discount) || 0);
      }, 0);

  const formatValue = (val: number) => {
    if (val >= 10000000) return "₹" + (val / 10000000).toFixed(1) + "Cr";
    if (val >= 100000) return "₹" + (val / 100000).toFixed(1) + "L";
    return "₹" + Number(val || 0).toLocaleString("en-IN");
  };

  const stats = [
    { label: "Total Quotations", value: total.toString(), icon: "ri-file-text-line", color: "bg-indigo-100 text-indigo-600" },
    { label: "Pending Approval", value: pending.toString(), icon: "ri-hourglass-line", color: "bg-amber-100 text-amber-600" },
    { label: "Accepted", value: accepted.toString(), icon: "ri-check-double-line", color: "bg-green-100 text-green-600" },
    { label: "Total Value", value: formatValue(totalValue), icon: "ri-money-rupee-circle-line", color: "bg-purple-100 text-purple-600" },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {stats.map((s) => (
        <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
          <div className={`w-11 h-11 rounded-lg ${s.color} flex items-center justify-center flex-shrink-0`}>
            <i className={`${s.icon} text-lg`} />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-gray-500 font-medium">{s.label}</p>
            <p className="text-lg font-bold text-gray-900">{s.value}</p>
          </div>
        </div>
      ))}
    </div>
  );
}